import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { toast } from 'sonner';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Unlink, ImagePlus, Undo2, Redo2, Loader2 } from 'lucide-react';

const toHtml = (text = '') => (/<[a-z][\s\S]*>/i.test(text) ? text : text.split(/\n\s*\n/).filter(Boolean).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`).join(''));

const Btn = ({ onClick, active, title, children, testId, disabled }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} disabled={disabled} data-testid={testId}
    className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${active ? 'bg-[#3B164C] text-white' : 'text-[#1E1622]/70 hover:bg-[#F5EFE9]'}`}>
    {children}
  </button>
);

const Toolbar = ({ editor, onImage, uploading }) => {
  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link address (e.g. https://… or /shop)', prev);
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const c = () => editor.chain().focus();
  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-[#E6DAC5] bg-[#FAF7F2] rounded-t-xl sticky top-0 z-10" data-testid="rte-toolbar">
      <Btn title="Heading" testId="rte-h2" active={editor.isActive('heading', { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
      <Btn title="Subheading" testId="rte-h3" active={editor.isActive('heading', { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
      <Btn title="Bold" testId="rte-bold" active={editor.isActive('bold')} onClick={() => c().toggleBold().run()}><Bold size={16} /></Btn>
      <Btn title="Italic" testId="rte-italic" active={editor.isActive('italic')} onClick={() => c().toggleItalic().run()}><Italic size={16} /></Btn>
      <Btn title="Bullet list" testId="rte-ul" active={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()}><List size={16} /></Btn>
      <Btn title="Numbered list" testId="rte-ol" active={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
      <Btn title="Quote" testId="rte-quote" active={editor.isActive('blockquote')} onClick={() => c().toggleBlockquote().run()}><Quote size={16} /></Btn>
      <Btn title="Add link" testId="rte-link" active={editor.isActive('link')} onClick={setLink}><Link2 size={16} /></Btn>
      <Btn title="Remove link" testId="rte-unlink" disabled={!editor.isActive('link')} onClick={() => c().unsetLink().run()}><Unlink size={16} /></Btn>
      <Btn title="Insert image" testId="rte-image" disabled={uploading} onClick={onImage}>{uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}</Btn>
      <span className="flex-1" />
      <Btn title="Undo" testId="rte-undo" onClick={() => c().undo().run()}><Undo2 size={16} /></Btn>
      <Btn title="Redo" testId="rte-redo" onClick={() => c().redo().run()}><Redo2 size={16} /></Btn>
    </div>
  );
};

// uploadImage(file) must resolve to an absolute or /api/files URL.
export const RichTextEditor = ({ value, onChange, uploadImage }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: false }), Link.configure({ openOnClick: false, autolink: true }), Image],
    content: toHtml(value),
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: 'article-body min-h-[260px] p-4 focus:outline-none', 'data-testid': 'rte-content' } },
  });
  useEffect(() => () => editor?.destroy(), [editor]);

  const onFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImage(f);
      const alt = window.prompt('Describe this image (helps Google and screen readers)', '') || '';
      editor.chain().focus().setImage({ src: url, alt }).run();
    } catch { toast.error('Image upload failed'); }
    setUploading(false);
  };

  if (!editor) return null;
  return (
    <div className="border border-[#E6DAC5] rounded-xl focus-within:border-[#3B164C]" data-testid="rich-text-editor">
      <Toolbar editor={editor} uploading={uploading} onImage={() => fileRef.current.click()} />
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/*" hidden data-testid="rte-image-input" onChange={(e) => { onFile(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
};
